<template>
  <AuthView title="Email Preview">
    <div class="mb-7">
      <AppRow>
        <div>
          <AppSelect
            v-model="form"
            label="Form (DB)"
            :items="forms"
            :item-title="(f: VisaForm) => `${f.client.name} (${f.country})`"
            return-object
          />
          <AppRow>
            <VisaCountrySelect v-model="form.country" label="Country" />
            <AppSelect
              v-model="form.product"
              :items="products.filter(p => p.country == form.country)"
              label="Product"
              item-title="name"
              return-object
            />
          </AppRow>
          <AppRow>
            <AppSelect v-model="template" :items="EMAIL_TEMPLATES" label="Template" />
            <AppBtn primary @click="preview">Preview</AppBtn>
          </AppRow>
          <JsonEditor v-model="emailProps" class="json-editor" />
          <TextArea v-model.lazy="result" label="HTML" />
        </div>
        <iframe :srcdoc="result" width="100%" height="800" />
      </AppRow>
    </div>
  </AuthView>
</template>

<script setup lang="ts">
import { EMAIL_TEMPLATES } from '~/assets/constants';
import { snackbarInjectionKey } from '~/assets/injection-keys';
import { couponFactory } from '~/factories/mini-factories';
import { getAllProducts, getForms, getOrder, previewEmail } from '~/helpers/http';
import type { EmailPropsMap, EmailTemplate, Order, VisaForm } from '~/types';

definePageMeta({ middleware: 'admin' });

const snackbar = inject(snackbarInjectionKey)!;

const products = await getAllProducts();
const forms = await getForms({});
const form = ref(forms[0]);
const order = ref<Order>();
const template = ref<EmailTemplate>('contact');
const result = ref('');

const formToSystemEmailData = computed<EmailPropsMap['new_order']>(() => ({ form: form.value, order: order.value! }));
const formAbandonmentEmailData = computed<EmailPropsMap['form_abandonment']>(() => ({ form: form.value }));
const resumeUrlEmailData = computed<EmailPropsMap['resume_url']>(() => ({
  url: 'https://google.com',
  form: form.value
}));
const couponUsedEmailData = computed<EmailPropsMap['coupon_used']>(() => ({ form: form.value, order: order.value! }));
const orderRecievedEmailData = computed<EmailPropsMap['order_recieved']>(() => ({
  id: order.value?.id || '123',
  form: form.value
}));
const approvedEmailData = computed<EmailPropsMap['approved']>(() => ({
  form: form.value,
  visaLinks: form.value.applicants.map(() => ({ number: 'V10101010', url: 'https://google.com' }))
}));
const contactMessageTemplateData = computed<EmailPropsMap['contact']>(() => ({
  name: form.value.client.name || 'John Doe',
  email: form.value.client.email || 'hey.stupid@google.com',
  msg: 'Hello, I would like to know more about your services.'
}));
const billRequestEmailData = computed<EmailPropsMap['bill_request']>(() => ({
  form: form.value,
  company: { name: 'Tesla', inn: '1234567890', kpp: '0987654321', address: 'Tel Aviv' }
}));
const mobilePaymentEmailData = computed<EmailPropsMap['mobile_payment']>(() => ({
  form: form.value,
  phone: '0501234567',
  transferId: '123456'
}));
const mobilePaymentReceivedEmailData = computed<EmailPropsMap['mobile_payment_received']>(() => ({
  form: form.value,
  processor: 'bit'
}));
const billRequestReceivedEmailData = computed<EmailPropsMap['bill_request_received']>(() => ({
  form: form.value,
  company: { name: 'Tesla', inn: '1234567890', kpp: '0987654321', address: 'Tel Aviv' },
  productName: 'Visa to Russia',
  totalPrice: '1000 RUB'
}));
const oneTimeCouponEmailData = computed<EmailPropsMap['onetime_coupon']>(() => ({
  form: form.value,
  coupon: couponFactory('VISANET10')
}));
const statusEmailData = computed<EmailPropsMap['status']>(() => ({ form: form.value, status: 'active' }));
const fixEmailData = computed<EmailPropsMap['fix']>(() => {
  return {
    form: form.value,
    applicant: form.value.applicants[0],
    email: form.value.client.email || 'johndoe@gmail.com',
    source: 'form',
    issues: {
      face_photo: [{ value: 'photo_is_missing', label: 'Photo is Missing' }],
      passport_photo: [],
      business: [],
      passport_expiry: [],
      application_details: []
    }
  };
});
const emailProps = computed(() => {
  const materials: { [T in EmailTemplate]: EmailPropsMap[T] } = {
    new_order: formToSystemEmailData.value,
    form_abandonment: formAbandonmentEmailData.value,
    resume_url: resumeUrlEmailData.value,
    coupon_used: couponUsedEmailData.value,
    order_recieved: orderRecievedEmailData.value,
    approved: approvedEmailData.value,
    contact: contactMessageTemplateData.value,
    bill_request: billRequestEmailData.value,
    mobile_payment: mobilePaymentEmailData.value,
    mobile_payment_received: mobilePaymentReceivedEmailData.value,
    bill_request_received: billRequestReceivedEmailData.value,
    onetime_coupon: oneTimeCouponEmailData.value,
    status: statusEmailData.value,
    fix: fixEmailData.value,
    wrapper: {},
  };
  return materials[template.value];
});

watch(form, async form => {
  if (!form || !form.orderId) return;
  const _order = await getOrder(form.orderId);
  if (!_order || 'success' in _order) throw new Error('Failed to load order');
  order.value = _order;
});

watch(() => form.value.country, c => {
  form.value.product = products.find(p => p.country == c)!;
});

async function preview() {
  const html = await previewEmail({
    template: template.value,
    props: emailProps.value
  }).catch(() => null);
  if (!html) {
    snackbar.value!.showError('Error');
    return;
  }
  result.value = html;
}
</script>

<style scoped>
.json-editor {
  margin-bottom: var(--space-6);
  max-height: 600px;
  overflow-y: auto;
}
</style>